import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchNotificationsStatus, markAllNotificationsRead } from '../../lib/api';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['notifications', 'full'],
    queryFn: () => fetchNotificationsStatus(true),
    enabled: open,
    refetchInterval: open ? 60_000 : false,
    staleTime: 30_000,
  });

  const queryClient = useQueryClient();
  const notifications = query.data?.notifications || [];
  const unreadCount = query.data?.unread_count || 0;

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
    } catch { /* best-effort */ }
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const typeIcon: Record<string, string> = {
    payment: '💰',
    invoice: '🧾',
    opportunity: '⇢',
    sync: '⇄',
    alert: '🔴',
  };

  const formatTime = (ts: string) => {
    try {
      const d = new Date(ts);
      const diff = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diff < 1) return 'maintenant';
      if (diff < 60) return `il y a ${diff} min`;
      if (diff < 1440) return `il y a ${Math.floor(diff / 60)} h`;
      return d.toLocaleDateString('fr-FR');
    } catch {
      return '';
    }
  };

  return (
    <div className="notification-bell" ref={ref}>
      <button
        type="button"
        className="shell__header-link notification-bell__trigger"
        onClick={() => setOpen(!open)}
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} non lues)` : ''}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9a6 6 0 0112 0c0 5 2 6 2 7H4c0-1 2-2 2-7zM10 20a2 2 0 004 0" /></svg>
        {unreadCount > 0 && <span className="notification-bell__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>
      {open && (
        <div className="notification-bell__dropdown">
          <div className="notification-bell__header">
            <strong>Notifications</strong>
            {unreadCount > 0 && (
              <>
                <span className="notification-bell__count">{unreadCount} non lue(s)</span>
                <button type="button" className="button button--ghost" style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)' }} onClick={markAllRead}>
                  Tout marquer lu
                </button>
              </>
            )}
          </div>
          <div className="notification-bell__list">
            {notifications.length === 0 && (
              <div className="notification-bell__empty">Aucune notification</div>
            )}
            {notifications.slice(0, 20).map((n) => (
              <div key={n.id} className={`notification-bell__item${n.read ? '' : ' is-unread'}`}>
                <span className="notification-bell__icon">{typeIcon[n.type] || '📌'}</span>
                <div className="notification-bell__content">
                  <div className="notification-bell__message">{n.message}</div>
                  <div className="notification-bell__time">{formatTime(n.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
